// NFT drops (spec 8.3) and player collections (8.5): daily drops announced the day before,
// whitelist by engaging, NPC mint-outs, floor dynamics, listings, and royalties.

import { TUNABLES } from "@degen/shared/config/tunables";
import { NFT_COLLECTION_NAMES } from "@degen/shared/config/content";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { addTrust, playerClout } from "../player";
import { sentimentBuyMultiplier } from "../market/sentiment";
import { money } from "@degen/shared/money";

const N = TUNABLES.nft;
const L = TUNABLES.launch;
const TPD = TUNABLES.clock.ticksPerDay;

export type ArtStyle = "faces" | "blobs" | "glyphs" | "gradients";
export const ART_STYLES: ArtStyle[] = ["faces", "blobs", "glyphs", "gradients"];

export type CollectionPhase = "announced" | "minting" | "minted_out" | "closed";

export interface Listing {
  price: number;
  ids: number[];
}

export interface Collection {
  id: string;
  name: string;
  creator: "player" | "npc";
  style: ArtStyle;
  seed: number;
  supply: number;
  mintPrice: number;
  wlPrice: number;
  announceTick: number;
  mintStart: number;
  mintEnd: number;
  hype: number;
  demand: number; // hidden: fraction of supply NPCs want during the window (>= 1 mints out)
  minted: number;
  phase: CollectionPhase;
  mintedOutTick?: number;
  floor: number;
  floorHistory: number[]; // hourly
  wl: boolean; // player has a whitelist spot
  playerMinted: number;
  owned: number[]; // token numbers the player holds
  listing?: Listing;
  // player-launched collections
  revenue: number;
  royaltiesUntil?: number;
  lastActiveDay?: number;
  abandoned?: boolean;
}

export interface NftState {
  collections: Collection[];
  nextId: number;
}

export function createNft(): NftState {
  return { collections: [], nextId: 1 };
}

export function nftValue(state: GameState): number {
  let v = 0;
  for (const c of state.nft.collections) v += c.owned.length * (c.phase === "announced" || c.phase === "minting" ? c.mintPrice : c.floor);
  return v;
}

function uniqueName(state: GameState, rng: Rng): string {
  const used = new Set(state.nft.collections.map((c) => c.name));
  for (let i = 0; i < 12; i++) {
    const n = rng.pick(NFT_COLLECTION_NAMES);
    if (!used.has(n)) return n;
  }
  return `${rng.pick(NFT_COLLECTION_NAMES)} ${rng.int(2, 9)}`;
}

function newCollection(state: GameState, rng: Rng, c: Partial<Collection> & Pick<Collection, "name" | "creator" | "supply" | "mintPrice" | "mintStart" | "mintEnd" | "hype">): Collection {
  const col: Collection = {
    id: `c${state.nft.nextId++}`,
    style: rng.pick(ART_STYLES),
    seed: rng.seed(),
    wlPrice: Math.round(c.mintPrice * (1 - N.wlDiscount) * 100) / 100,
    announceTick: state.tick,
    demand: 0,
    minted: 0,
    phase: "announced",
    floor: c.mintPrice,
    floorHistory: [],
    wl: false,
    playerMinted: 0,
    owned: [],
    revenue: 0,
    ...c,
  };
  state.nft.collections.push(col);
  return col;
}

/** Day boundary, BEFORE daily counters reset: schedule tomorrow's drops, abandonment, pruning. */
export function nftNewDay(state: GameState, rng: Rng, day: number) {
  const n = rng.int(N.dropsPerDay[0], N.dropsPerDay[1]);
  const tomorrow = day * TPD; // tick at 00:00 of day+1
  for (let i = 0; i < n; i++) {
    const start = tomorrow + rng.int(N.mintStartHour[0] * 60, N.mintStartHour[1] * 60);
    const col = newCollection(state, rng, {
      name: uniqueName(state, rng),
      creator: "npc",
      supply: rng.pick(N.supplies),
      mintPrice: Math.round(rng.range(N.mintPriceUsd[0], N.mintPriceUsd[1])),
      mintStart: start,
      mintEnd: start + rng.int(N.mintDurationTicks[0], N.mintDurationTicks[1]),
      hype: rng.range(N.hype[0], N.hype[1]),
    });
    pushLog(state, "nft_announced", `${col.name} mints tomorrow. Engage for WL 👀`, { collection: col.id });
  }

  // Player collections: posting keeps them alive; going silent for 5 days kills them.
  const p = state.player;
  for (const c of state.nft.collections) {
    if (c.creator !== "player" || c.abandoned || c.royaltiesUntil === undefined) continue;
    if (p.daily.posts > 0) c.lastActiveDay = day;
    if (state.tick > c.royaltiesUntil) continue;
    if (day - (c.lastActiveDay ?? day) >= L.abandonDays) {
      c.abandoned = true;
      c.floor *= 0.5;
      c.hype = 0;
      addTrust(p, -L.abandonTrust);
      pushLog(state, "abandoned", `You ghosted ${c.name} for ${L.abandonDays} days. Holders call it abandoned. Trust down.`, { collection: c.id });
    }
  }

  // Forget old collections the player has nothing to do with.
  const cutoff = state.tick - N.keepDays * TPD;
  state.nft.collections = state.nft.collections.filter((c) => c.mintEnd > cutoff || c.owned.length > 0 || c.creator === "player");
}

/** Per tick: mint progress, floors, listings, royalties. */
export function nftTick(state: GameState, rng: Rng) {
  const tick = state.tick;
  const sent = sentimentBuyMultiplier(state.market.sentiment);
  for (const c of state.nft.collections) {
    if (c.phase === "announced" && tick >= c.mintStart) {
      c.phase = "minting";
      // Hidden demand decided at mint start: hype, sentiment, luck.
      c.demand = Math.pow(c.hype / 100, 1.3) * sent * rng.range(0.6, 1.8) * (c.creator === "player" ? 1 : 1.4);
      pushLog(state, "nft_mint_live", `${c.name} mint is live!`, { collection: c.id });
    }
    if (c.phase === "minting") {
      const dur = c.mintEnd - c.mintStart;
      const perTick = (c.supply * c.demand) / dur;
      const add = Math.min(c.supply - c.minted, Math.max(0, Math.round(perTick * rng.range(0.4, 1.6))));
      c.minted += add;
      if (c.creator === "player" && add > 0) {
        const rev = add * c.mintPrice * (1 - N.marketFee);
        state.player.cash += rev;
        c.revenue += rev;
      }
      if (c.minted >= c.supply) {
        c.phase = "minted_out";
        c.mintedOutTick = tick;
        const speed = 1 - (tick - c.mintStart) / dur; // 1 = instant
        c.floor = c.mintPrice * (N.mintOutFloorMult[0] + (N.mintOutFloorMult[1] - N.mintOutFloorMult[0]) * speed);
        c.hype = Math.min(100, c.hype + 15);
        pushLog(state, "nft_minted_out", `${c.name} minted out! Floor ${Math.round(c.floor / c.mintPrice * 10) / 10}x mint.`, { collection: c.id });
        if (c.creator === "player") c.royaltiesUntil = tick + L.royaltyDays * TPD;
      } else if (tick >= c.mintEnd) {
        c.phase = "closed";
        const frac = c.minted / c.supply;
        c.floor = c.mintPrice * (N.failFloorMult[0] + (N.failFloorMult[1] - N.failFloorMult[0]) * frac);
        c.hype *= 0.5;
        pushLog(state, "nft_failed", `${c.name} didn't mint out (${Math.round(frac * 100)}%). Floor below mint.`, { collection: c.id });
        if (c.creator === "player") c.royaltiesUntil = tick + L.royaltyDays * TPD;
      }
    }
    if (c.phase !== "minted_out" && c.phase !== "closed") continue;

    // Hourly floor drift with hype decay and the odd spike.
    if (tick % 60 === 0) {
      c.hype *= N.hypeDecayPerHour;
      const drift = (c.hype - 30) / 3000;
      c.floor = Math.max(1, c.floor * Math.exp(drift + rng.normal() * N.floorVolatility));
      if (rng.chance(N.floorSpikeChancePerHour)) c.floor *= rng.range(1.3, 2);
      c.floorHistory.push(c.floor);
      if (c.floorHistory.length > 24 * 14) c.floorHistory.shift();
      // Royalties on secondary volume for the player's own collection.
      if (c.creator === "player" && c.royaltiesUntil !== undefined && tick <= c.royaltiesUntil && !c.abandoned) {
        const vol = c.floor * c.minted * 0.002 * (c.hype / 50) * rng.range(0.5, 1.5);
        const roy = vol * N.royalty;
        state.player.cash += roy;
        c.revenue += roy;
      }
    }

    // Player listing fills.
    if (c.listing && c.listing.ids.length) {
      const ratio = c.listing.price / c.floor;
      const mult = ratio <= 1 ? 3 : ratio <= 1.2 ? 1 : ratio <= 1.6 ? 0.3 : 0.03;
      if (rng.chance(N.listingFillBase * (0.3 + c.hype / 50) * mult)) {
        const id = c.listing.ids.shift()!;
        c.owned = c.owned.filter((x) => x !== id);
        const net = c.listing.price * (1 - N.marketFee - (c.creator === "player" ? 0 : N.royalty));
        state.player.cash += net;
        pushLog(state, "nft_sold", `Sold ${c.name} #${id} for ${money(c.listing.price)}.`, { collection: c.id });
        if (!c.listing.ids.length) c.listing = undefined;
      }
    }
  }
}

// ---------------------------------------------------------------- player actions

export type NftResult = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function engageForWl(state: GameState, id: string): NftResult {
  const c = state.nft.collections.find((x) => x.id === id);
  if (!c || c.phase !== "announced") return { ok: false, error: "WL is closed for that one." };
  if (c.wl) return { ok: false, error: "You already have a WL spot." };
  c.wl = true;
  return { ok: true };
}

export function mintNft(state: GameState, rng: Rng, id: string, qty: number): NftResult {
  const c = state.nft.collections.find((x) => x.id === id);
  const p = state.player;
  if (!c) return { ok: false, error: "No such collection." };
  if (c.creator === "player") return { ok: false, error: "Minting your own collection doesn't count as demand, ser." };
  const wlMint = c.wl && c.playerMinted === 0;
  // WL spots are guaranteed even if the public mint sells out mid-window.
  const open = c.phase === "minting" || (wlMint && c.phase === "minted_out" && state.tick < c.mintEnd);
  if (!open) return { ok: false, error: c.phase === "announced" ? "Mint hasn't started yet." : "Mint is over. Check the floor." };
  qty = Math.max(1, Math.min(qty | 0, N.maxPerWallet + (c.wl ? 1 : 0) - c.playerMinted));
  if (qty <= 0) return { ok: false, error: "Wallet limit reached." };
  if (!wlMint) qty = Math.min(qty, c.supply - c.minted);
  if (qty <= 0) return { ok: false, error: "Minted out." };
  const price = (i: number) => (wlMint && i === 0 ? c.wlPrice : c.mintPrice);
  let cost = 0;
  for (let i = 0; i < qty; i++) cost += price(i);
  if (p.cash < cost) return { ok: false, error: "Not enough cash." };
  p.cash -= cost;
  for (let i = 0; i < qty; i++) {
    let num = rng.int(1, c.supply);
    while (c.owned.includes(num)) num = (num % c.supply) + 1;
    c.owned.push(num);
  }
  if (!wlMint || qty > 1) c.minted = Math.min(c.supply, c.minted + (wlMint ? qty - 1 : qty));
  c.playerMinted += qty;
  return { ok: true, data: { qty, cost } };
}

export function listNft(state: GameState, id: string, qty: number, price: number): NftResult {
  const c = state.nft.collections.find((x) => x.id === id);
  if (!c || !c.owned.length) return { ok: false, error: "You don't own any." };
  if (c.phase === "announced" || c.phase === "minting") return { ok: false, error: "Wait for the mint to end." };
  if (!(price > 0)) return { ok: false, error: "Set a price." };
  const n = Math.max(1, Math.min(qty | 0, c.owned.length));
  c.listing = { price, ids: c.owned.slice(0, n) };
  return { ok: true };
}

export function cancelListing(state: GameState, id: string): NftResult {
  const c = state.nft.collections.find((x) => x.id === id);
  if (!c?.listing) return { ok: false, error: "Nothing listed." };
  c.listing = undefined;
  return { ok: true };
}

/** Dump into the top bid (floor minus a discount), instantly. */
export function sellNftNow(state: GameState, id: string, qty: number): NftResult {
  const c = state.nft.collections.find((x) => x.id === id);
  if (!c || !c.owned.length) return { ok: false, error: "You don't own any." };
  if (c.phase === "announced" || c.phase === "minting") return { ok: false, error: "Wait for the mint to end." };
  const n = Math.max(1, Math.min(qty | 0, c.owned.length));
  const bid = c.floor * (1 - N.instantSellDiscount);
  const net = n * bid * (1 - N.marketFee - (c.creator === "player" ? 0 : N.royalty));
  const sold = c.owned.splice(0, n);
  if (c.listing) {
    c.listing.ids = c.listing.ids.filter((x) => !sold.includes(x));
    if (!c.listing.ids.length) c.listing = undefined;
  }
  state.player.cash += net;
  c.floor *= 1 - 0.01 * n; // dumping into bids nudges the floor down
  return { ok: true, data: { qty: n, usd: net } };
}

export interface LaunchCollectionInput {
  name: string;
  supply: number;
  mintPrice: number;
  style: ArtStyle;
}

export function launchCollection(state: GameState, rng: Rng, input: LaunchCollectionInput): NftResult {
  const p = state.player;
  const name = input.name.trim();
  if (Array.from(name).length < 3 || Array.from(name).length > 24) return { ok: false, error: "Name: 3-24 characters." };
  if (!(L.collectionSupplies as readonly number[]).includes(input.supply)) return { ok: false, error: "Pick a supply." };
  if (!(input.mintPrice >= 1 && input.mintPrice <= 5_000)) return { ok: false, error: `Mint price: ${money(1)}-${money(5_000)}.` };
  if (p.cash < L.collectionFeeUsd) return { ok: false, error: `Setup costs ${money(L.collectionFeeUsd)}.` };
  p.cash -= L.collectionFeeUsd;
  const clout = playerClout(p);
  const reach = clout / (clout + 5_000);
  // Price matters: overpriced mints scare people off.
  const priceFit = Math.min(1.3, 120 / Math.max(20, input.mintPrice)) ** 0.4;
  const hype = Math.min(100, (15 + 55 * reach * (p.trust / 60) + state.market.sentiment.value * 10 + rng.range(0, 15)) * priceFit);
  const start = state.tick + L.collectionMintDelayTicks;
  const c = newCollection(state, rng, {
    name,
    creator: "player",
    style: input.style,
    supply: input.supply,
    mintPrice: input.mintPrice,
    mintStart: start,
    mintEnd: start + L.collectionMintTicks,
    hype,
    lastActiveDay: dayOf(state.tick),
  });
  p.stats.collectionsLaunched++;
  pushLog(state, "collection_launched", `You launched ${c.name}: ${c.supply} supply at ${money(c.mintPrice)}. Mint opens in an hour.`, { collection: c.id });
  return { ok: true };
}
